<template>
    <div
        class="flex h-screen w-screen flex-col overflow-hidden"
        :class="{ 'bg-ds-primary-900 text-white': darkMode }"
    >
        <div class="fixed top-0 z-10 flex w-screen gap-2 bg-ds-primary-900 p-2 lg:justify-end">
            <h1 class="flex-1 px-2 text-white xs:hidden lg:block">Closinglock Design System</h1>
            <DsButton
                class="lg:hidden"
                icon="fa-solid fa-list"
                size="sm"
                variant="primary"
                @click="toggleComponentList"
            >
                {{ currentRouteName }}
                <DsIcon
                    icon="fa-solid fa-chevron-right"
                    size="sm"
                />
            </DsButton>
            <DsButton
                size="sm"
                variant="primary"
                @click="darkMode = !darkMode"
            >
                Switch to {{ darkMode ? 'Light Background' : 'Dark Background' }}
            </DsButton>

            <!-- Mobile component list -->
            <Teleport to="body">
                <div
                    class="absolute left-0 top-0 z-60 h-screen w-screen overflow-y-auto bg-ds-primary-900 p-6 text-white transition-all duration-200 ease-in-out"
                    :class="[showComponentList ? 'translate-x-0' : '-translate-x-full']"
                >
                    <div class="flex w-full flex-col gap-2">
                        <div class="flex justify-between">
                            <h2>Component library</h2>
                            <DsButton
                                class="w-12 text-right"
                                destructive
                                leading="fa-solid fa-xmark"
                                size="sm"
                                variant="tertiary"
                                @click="toggleComponentList"
                            >
                                Close
                            </DsButton>
                        </div>
                        <template
                            v-for="route in sampleRoutes"
                            :key="route.path"
                        >
                            <div
                                v-if="route.children"
                                class="mb-2"
                            >
                                <div class="p-2 font-semibold">
                                    {{ route.name }}
                                </div>
                                <div
                                    v-for="child in route.children"
                                    :key="`${route.path}/${child.path}`"
                                    class="flex p-2 pl-6"
                                >
                                    <router-link
                                        :to="`${route.path}/${child.path}`"
                                        @click="toggleComponentList"
                                    >
                                        {{ child.name }}
                                    </router-link>
                                </div>
                            </div>
                            <div
                                v-else
                                class="flex p-2"
                            >
                                <router-link
                                    :to="route.path"
                                    @click="toggleComponentList"
                                >
                                    {{ route.name }}
                                </router-link>
                            </div>
                        </template>
                    </div>
                </div>
            </Teleport>
        </div>
        <div class="relative mx-auto flex w-full flex-1 overflow-hidden p-2 pt-14">
            <div class="flex h-full w-2/12 gap-4 xs:hidden lg:flex">
                <div
                    class="grid h-full flex-1 overflow-y-auto rounded-lg border border-ds-primary-200 p-2 shadow-lg"
                    :class="{
                        'bg-ds-primary-50 text-content-primary': !darkMode,
                        'bg-ds-primary-600 text-white': darkMode,
                    }"
                >
                    <nav class="space-y-2">
                        <template
                            v-for="route in sampleRoutes"
                            :key="route.path"
                        >
                            <div
                                v-if="route.children"
                                class="mb-2"
                            >
                                <div class="p-2 font-semibold text-content-secondary dark:text-ds-primary-300">
                                    {{ route.name }}
                                </div>
                                <router-link
                                    v-for="child in route.children"
                                    :key="`${route.path}/${child.path}`"
                                    class="block p-2 pl-6"
                                    :class="{
                                        'router-link-active': $route.path === `${route.path}/${child.path}`,
                                    }"
                                    :to="`${route.path}/${child.path}`"
                                >
                                    {{ child.name }}
                                </router-link>
                            </div>
                            <router-link
                                v-else
                                class="block p-2"
                                :class="{
                                    'router-link-active': $route.path === route.path,
                                }"
                                :to="route.path"
                            >
                                {{ route.name }}
                            </router-link>
                        </template>
                    </nav>
                </div>
            </div>
            <div class="h-full overflow-y-auto px-4 xs:w-full lg:w-10/12">
                <div
                    v-if="currentRouteInfo"
                    class="mb-6"
                >
                    <h1
                        v-if="currentRouteInfo.parent"
                        class="mb-2 text-3xl font-bold"
                    >
                        {{ currentRouteInfo.parent }}
                    </h1>
                    <h2
                        v-if="currentRouteInfo.parent && currentRouteInfo.child"
                        class="text-2xl text-ds-primary-600 dark:text-ds-primary-400"
                    >
                        {{ currentRouteInfo.child }}
                    </h2>
                    <h1
                        v-if="!currentRouteInfo.parent"
                        class="text-3xl font-bold"
                    >
                        {{ currentRouteInfo.name }}
                    </h1>
                </div>
                <router-view></router-view>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { buildSampleRouter } from '@/components/design-system/design-samples/sample-routes';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { computed, ref } from 'vue';
import type { RouteRecord } from 'vue-router';
import { useRouter } from 'vue-router';

const darkMode = ref(false);
const showComponentList = ref(false);
const router = buildSampleRouter();
const BASE_PATH = 'design';

function toggleComponentList() {
    showComponentList.value = !showComponentList.value;
}

// Process routes to handle nested structure
interface ProcessedRoute {
    path: string;
    name: string;
    children?: ProcessedRoute[];
}

function processRoutes(routes: RouteRecord[]): ProcessedRoute[] {
    const processedRoutes: ProcessedRoute[] = [];

    routes.forEach(route => {
        // Skip the base redirect route and non-design routes
        if (route.path === '/design/' || !route.path.startsWith('/design/')) {
            return;
        }

        // Only process top-level routes (count slashes to determine level)
        const slashCount = route.path.split('/').length - 1;
        if (slashCount > 2) {
            return; // Skip nested routes as they'll be handled by their parent
        }

        const processedRoute: ProcessedRoute = {
            path: route.path,
            name: deriveNameFromPath(route.path),
        };

        if (route.children && route.children.length > 0) {
            processedRoute.children = route.children
                .filter(child => !child.redirect)
                .map(child => ({
                    path: child.path,
                    name: deriveNameFromPath(child.path),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        }

        processedRoutes.push(processedRoute);
    });

    return processedRoutes.sort((a, b) => a.name.localeCompare(b.name));
}

function deriveNameFromPath(path: string): string {
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];

    // Handle empty path (like redirect routes)
    if (!lastSegment) {
        return '';
    }

    return lastSegment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const sampleRoutes = processRoutes(router.getRoutes());

const currentRouter = useRouter();
router.getRoutes().forEach(route => {
    if (!currentRouter.hasRoute(route.name as string)) {
        currentRouter.addRoute(route);
    }
});
const currentRouteName = computed(() => {
    const currentPath = currentRouter.currentRoute.value.path;

    // First check top-level routes
    for (const route of sampleRoutes) {
        if (route.path === currentPath) {
            return route.name;
        }

        // Check children if they exist
        if (route.children) {
            for (const child of route.children) {
                if (`${route.path}/${child.path}` === currentPath) {
                    return `${route.name} > ${child.name}`;
                }
            }
        }
    }

    return '';
});

const currentRouteInfo = computed(() => {
    const currentPath = currentRouter.currentRoute.value.path;

    // First check top-level routes
    for (const route of sampleRoutes) {
        if (route.path === currentPath) {
            return {
                name: route.name,
                parent: null,
                child: null,
            };
        }

        // Check children if they exist
        if (route.children) {
            for (const child of route.children) {
                if (`${route.path}/${child.path}` === currentPath) {
                    return {
                        name: `${route.name} > ${child.name}`,
                        parent: route.name,
                        child: child.name,
                    };
                }
            }
        }
    }

    return null;
});

/** Forces router to resolve again to correctly interpret the dynamically added routes
 * and goes to /colors if no path is provided
 */
currentRouter.replace({ path: location.pathname && location.pathname !== '/' ? location.pathname : `/${BASE_PATH}/colors` });
</script>

<style scoped>
.router-link-active {
    @apply bg-ds-primary-700 text-white;
}

.dark .dark\:text-ds-primary-300 {
    color: rgb(209 213 219);
}
dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
}
</style>
